import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

import { useToast } from "./ui/use-toast";

export default function VehicleForm({ vehicle, onSuccess, onCancel }) {
  const [plateNumber, setPlateNumber] = useState(vehicle?.plate_number || "");
  const [carType, setCarType] = useState(vehicle?.car_type || "");
  const [manufacturer, setManufacturer] = useState(vehicle?.manufacturer || "");
  const [model, setModel] = useState(vehicle?.model || "");
  const [yearOfManufacture, setYearOfManufacture] = useState(vehicle?.year_of_manufacture || "");
  const [companyName, setCompanyName] = useState(vehicle?.company_name || "");
  const [status, setStatus] = useState(vehicle?.status || "");
  const [roadStatus, setRoadStatus] = useState(vehicle?.road_status || "");
  const [engineChassis, setEngineChassis] = useState(vehicle?.engine_chassis || "");
  const [machineryNo, setMachineryNo] = useState(vehicle?.machinery_no || "");
  const [bdm, setBdm] = useState(vehicle?.bdm || "");
  const [btm, setBtm] = useState(vehicle?.btm || "");
  const [bg2, setBg2] = useState(vehicle?.bg2 || "");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(vehicle?.project?.id || "");


  const [insuranceData, setInsuranceData] = useState({ expiry_date: vehicle?.insurance?.expiry_date || "" });
  const [roadtaxData, setRoadtaxData] = useState({ expiry_date: vehicle?.roadtax?.expiry_date || "" });
  const [inspectionData, setInspectionData] = useState({ expiry_date: vehicle?.inspection?.expiry_date || "" });
  const [permitBrunei, setPermitBrunei] = useState({
    expiry_date: vehicle?.permits?.find(p => p.permit_type === "brunei")?.expiry_date || "",
    license_number: vehicle?.permits?.find(p => p.permit_type === "brunei")?.license_number || ""
  });

  const [permitSabah, setPermitSabah] = useState({
    expiry_date: vehicle?.permits?.find(p => p.permit_type === "sabah")?.expiry_date || "",
    license_number: vehicle?.permits?.find(p => p.permit_type === "sabah")?.license_number || ""
  });

  const [permitSarawak, setPermitSarawak] = useState({
    expiry_date: vehicle?.permits?.find(p => p.permit_type === "sarawak")?.expiry_date || "",
    license_number: vehicle?.permits?.find(p => p.permit_type === "sarawak")?.license_number || ""
  });
  const [hasInsurance, setHasInsurance] = useState(!!vehicle?.insurance);
  const [hasRoadtax, setHasRoadtax] = useState(!!vehicle?.roadtax);
  const [hasInspection, setHasInspection] = useState(!!vehicle?.inspection);
  const [hasPermitBrunei, setHasPermitBrunei] = useState(
      vehicle?.permits?.some(p => p.permit_type === "brunei") || false
  );
  const [hasPermitSabah, setHasPermitSabah] = useState(
      vehicle?.permits?.some(p => p.permit_type === "sabah") || false
  );
  const [hasPermitSarawak, setHasPermitSarawak] = useState(
      vehicle?.permits?.some(p => p.permit_type === "sarawak") || false
  );



  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Character count display function
  const getCharacterCountDisplay = (text, maxLength) => {
    const currentLength = text?.length || 0;
    const remaining = maxLength - currentLength;
    
    let colorClass = 'text-gray-500';
    if (remaining <= 5) {
      colorClass = 'text-red-500';
    } else if (remaining <= 10) {
      colorClass = 'text-yellow-500';
    } else if (remaining <= 20) {
      colorClass = 'text-green-500';
    }
    
    return (
      <div className={`text-xs mt-1 ${colorClass}`}>
        {currentLength}/{maxLength} characters
        {remaining <= 2 && <span className="ml-2 font-semibold">⚠️ Approaching limit!</span>}
      </div>
    );
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    
    api
        .get("/api/projects/")
        .then((res) => setProjects(res.data))
        .catch((err) => {
          console.error("Failed to load projects", err);
          const errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to load projects";
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
        });
  }, [isAuthenticated]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to access this feature.",
        variant: "destructive",
      });
      return;
    }

    if (!plateNumber.trim()) {
      toast({
        title: "Validation Error",
        description: "Plate number is required",
        variant: "destructive",
      });
      return;
    }

    if (!carType) {
      toast({
        title: "Validation Error",
        description: "Car type is required",
        variant: "destructive",
      });
      return;
    }

    if (!companyName) {
      toast({
        title: "Validation Error",
        description: "Company is required",
        variant: "destructive",
      });
      return;
    }

    if (!status) {
      toast({
        title: "Validation Error",
        description: "Status is required",
        variant: "destructive",
      });
      return;
    }

    if (!projectId) {
      toast({
        title: "Validation Error",
        description: "Deployment is required",
        variant: "destructive",
      });
      return;
    }

    const vehicleData = {
      plate_number: plateNumber.trim(),
      car_type: carType,
      manufacturer: manufacturer.trim() || null,
      model: model.trim() || null,
      year_of_manufacture: yearOfManufacture.trim() || null,
      company_name: companyName,
      status: status,
      road_status: roadStatus || null,
      engine_chassis: engineChassis.trim() || null,
      machinery_no: machineryNo.trim() || null,
      bdm: bdm.trim() || null,
      btm: btm.trim() || null,
      bg2: bg2.trim() || null,
      project_id: projectId,

    };

    const apiCall = vehicle
        ? api.put(`/api/vehicles/${vehicle.id}/`, vehicleData)
        : api.post("/api/vehicles/", vehicleData);

    apiCall
        .then(async (vehicleResponse) => {
          const vehicleId = vehicleResponse.data.id;

          // Create insurance if enabled
          if (hasInsurance && insuranceData.expiry_date) {
            try {
              await api.post("/api/insurances/", {
                vehicle: vehicleId,
                expiry_date: insuranceData.expiry_date,
              });
            } catch (insuranceError) {
              console.error("Failed to create insurance:", insuranceError);
              toast({
                title: "Warning",
                description: "Vehicle created but insurance failed to create. You can add it later.",
                variant: "destructive",
              });
            }
          }

          // Create roadtax if enabled
          if (hasRoadtax && roadtaxData.expiry_date) {
            try {
              await api.post("/api/roadtaxes/", {
                vehicle: vehicleId,
                expiry_date: roadtaxData.expiry_date,
              });
            } catch (roadtaxError) {
              console.error("Failed to create roadtax:", roadtaxError);
              toast({
                title: "Warning",
                description: "Vehicle created but roadtax failed to create. You can add it later.",
                variant: "destructive",
              });
            }
          }

          // Create inspection if enabled
          if (hasInspection && inspectionData.expiry_date) {
            try {
              await api.post("/api/inspections/", {
                vehicle: vehicleId,
                expiry_date: inspectionData.expiry_date,
              });
            } catch (inspectionError) {
              console.error("Failed to create inspection:", inspectionError);
              toast({
                title: "Warning",
                description: "Vehicle created but inspection failed to create. You can add it later.",
                variant: "destructive",
              });
            }
          }

          // Create permits if enabled
          const permitPromises = [];
          
          if (hasPermitBrunei && permitBrunei.expiry_date) {
            permitPromises.push(
              api.post("/api/permits/", {
                vehicle: vehicleId,
                permit_type: "brunei",
                expiry_date: permitBrunei.expiry_date,
                license_number: permitBrunei.license_number || null,
              })
            );
          }
          
          if (hasPermitSabah && permitSabah.expiry_date) {
            permitPromises.push(
              api.post("/api/permits/", {
                vehicle: vehicleId,
                permit_type: "sabah",
                expiry_date: permitSabah.expiry_date,
                license_number: permitSabah.license_number || null,
              })
            );
          }
          
          if (hasPermitSarawak && permitSarawak.expiry_date) {
            permitPromises.push(
              api.post("/api/permits/", {
                vehicle: vehicleId,
                permit_type: "sarawak",
                expiry_date: permitSarawak.expiry_date,
                license_number: permitSarawak.license_number || null,
              })
            );
          }
          
          // Wait for all permits to be created
          if (permitPromises.length > 0) {
            try {
              await Promise.all(permitPromises);
            } catch (permitError) {
              console.error("Failed to create some permits:", permitError);
              toast({
                title: "Warning",
                description: "Vehicle created but some permits failed to create. You can add them later.",
                variant: "destructive",
              });
            }
          }
          
          onSuccess(vehicleResponse.data);
          toast({
            title: "Success",
            description: "Vehicle saved successfully!",
          });
        })
        .catch((err) => {
          console.error(err);
          let errorMessage = "Failed to save vehicle";
          
          // Check for new error structure from custom exception handler
          if (err.response?.data?.details) {
            // Handle field-specific validation errors from custom handler
            const details = err.response.data.details;
            const fieldErrors = [];
            
            // Map field names to user-friendly names
            const fieldLabels = {
              'plate_number': 'Plate Number',
              'car_type': 'Car Type',
              'company_name': 'Company Name',
              'status': 'Status',
              'year_of_manufacture': 'Year of Manufacture',
              'engine_chassis': 'Engine/Chassis Number',
              'machinery_no': 'Machinery Number',
              'bdm': 'BDM',
              'btm': 'BTM',
              'bg2': 'BG2',
              'manufacturer': 'Manufacturer',
              'model': 'Model',
              'road_status': 'Road Status'
            };
            
            for (const [field, errors] of Object.entries(details)) {
              if (Array.isArray(errors)) {
                const fieldLabel = fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                fieldErrors.push(`${fieldLabel}: ${errors.join(', ')}`);
              }
            }
            
            if (fieldErrors.length > 0) {
              errorMessage = `Please fix the following errors:\n${fieldErrors.join('\n')}`;
            } else {
              errorMessage = err.response.data.message || "Validation failed";
            }
          }
          // Check for DRF validation errors (direct field errors)
          else if (err.response?.data && typeof err.response.data === 'object') {
            const fieldErrors = [];
            
            // Check if response.data contains field names as keys
            for (const [field, errors] of Object.entries(err.response.data)) {
              if (Array.isArray(errors) && field !== 'error' && field !== 'message' && field !== 'details') {
                const fieldLabels = {
                  'plate_number': 'Plate Number',
                  'car_type': 'Car Type',
                  'company_name': 'Company Name',
                  'status': 'Status',
                  'year_of_manufacture': 'Year of Manufacture',
                  'engine_chassis': 'Engine/Chassis Number',
                  'machinery_no': 'Machinery Number',
                  'bdm': 'BDM',
                  'btm': 'BTM',
                  'bg2': 'BG2',
                  'manufacturer': 'Manufacturer',
                  'model': 'Model',
                  'road_status': 'Road Status'
                };
                
                const fieldLabel = fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                fieldErrors.push(`${fieldLabel}: ${errors.join(', ')}`);
              }
            }
            
            if (fieldErrors.length > 0) {
              errorMessage = `Please fix the following errors:\n${fieldErrors.join('\n')}`;
            } else {
              errorMessage = err.response.data.message || err.response.data.error || "Validation failed";
            }
          }
          // Keep existing checks for backward compatibility
          else if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.response?.data?.error) {
            errorMessage = err.response.data.error;
          } else if (err.response?.data?.plate_number) {
            errorMessage = `Plate number error: ${err.response.data.plate_number.join(', ')}`;
          } else if (err.response?.data?.car_type) {
            errorMessage = `Car type error: ${err.response.data.car_type.join(', ')}`;
          } else if (err.response?.data?.company_name) {
            errorMessage = `Company name error: ${err.response.data.company_name.join(', ')}`;
          } else if (err.response?.data?.status) {
            errorMessage = `Status error: ${err.response.data.status.join(', ')}`;
          } else if (err.response?.data?.project) {
            errorMessage = `Deployment error: ${err.response.data.project.join(', ')}`;
          } else if (err.response?.status === 400) {
            errorMessage = "Invalid vehicle data provided";
          } else if (err.response?.status === 403) {
            errorMessage = "You do not have permission to create/update vehicles";
          } else if (err.response?.status === 409) {
            errorMessage = "A vehicle with this plate number already exists";
          } else if (err.response?.status === 500) {
            errorMessage = "Server error occurred. Please try again later";
          }
          
          toast({
            title: "Vehicle Operation Failed",
            description: errorMessage,
            variant: "destructive",
          });
        });
  };



  return (
      <form onSubmit={handleSubmit} className="space-y-6 p-4 max-w-4xl mx-auto">
        <h2 className="text-xl font-semibold">🚗 Vehicle Info</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Plate Number <span className="text-red-500">*</span> </Label><Input value={plateNumber} onChange={(e) => setPlateNumber(e.target.value)} disabled={!!vehicle} /></div>
          <div>
            <Label>Vehicle Type <span className="text-red-500">*</span> </Label>
            {vehicle ? (
                <Input value={carType} disabled />
            ) : (
                <Select value={carType} onValueChange={setCarType}>
                  <SelectTrigger><SelectValue placeholder="Select Vehicle Type" /></SelectTrigger>
                  <SelectContent>
                    {["barge", "backhoe_loader", "bulldozer", "company_car", "cement_truck", "excavator", "forklift", "grader", "jeep", "machinery", "mobile_crane", "motorcar", "motorcycle", "pick_up", "prime_mover", "rigid_diesel_tank_lorry", "rigid_lorry", "rigid_lorry_10_wheels", "rigid_lorry_6_wheels", "rigid_lorry_cargo_am", "rigid_lorry_cargo_am_with_crane", "rigid_lorry_vehicle_carrier", "road_paver", "road_roller", "semi_trailer_bitumen_tanker", "semi_trailer_low_loader", "shovel", "tractor", "trailer", "tugboat", "window_van"].sort().map((type) => (
                        <SelectItem key={type} value={type}>{type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()).replace(/\bAm\b/g, "AM")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
            )}
          </div>

          <div>
            <Label>Manufacturer</Label>
            <Input 
              value={manufacturer} 
              onChange={(e) => setManufacturer(e.target.value)} 
              maxLength={50}
            />
            {getCharacterCountDisplay(manufacturer, 50)}
          </div>
          
          <div>
            <Label>Model</Label>
            <Input 
              value={model} 
              onChange={(e) => setModel(e.target.value)} 
              maxLength={50}
            />
            {getCharacterCountDisplay(model, 50)}
          </div>
          
          <div>
            <Label>Year of Manufacture</Label>
            <Input 
              value={yearOfManufacture} 
              onChange={(e) => setYearOfManufacture(e.target.value)} 
              maxLength={30}
            />
            {getCharacterCountDisplay(yearOfManufacture, 30)}
          </div>
          
          <div>
            <Label>Engine / Chassis</Label>
            <Input 
              value={engineChassis} 
              onChange={(e) => setEngineChassis(e.target.value)} 
              maxLength={255}
            />
            {getCharacterCountDisplay(engineChassis, 255)}
          </div>
          
          <div>
            <Label>Machinery No.</Label>
            <Input 
              value={machineryNo} 
              onChange={(e) => setMachineryNo(e.target.value)} 
              maxLength={50}
            />
            {getCharacterCountDisplay(machineryNo, 50)}
          </div>
          
          <div>
            <Label>BDM</Label>
            <Input 
              value={bdm} 
              onChange={(e) => setBdm(e.target.value)} 
              maxLength={10}
            />
            {getCharacterCountDisplay(bdm, 10)}
          </div>
          
          <div>
            <Label>BTM</Label>
            <Input 
              value={btm} 
              onChange={(e) => setBtm(e.target.value)} 
              maxLength={10}
            />
            {getCharacterCountDisplay(btm, 10)}
          </div>
          
          <div>
            <Label>BG2</Label>
            <Input 
              value={bg2} 
              onChange={(e) => setBg2(e.target.value)} 
              maxLength={10}
            />
            {getCharacterCountDisplay(bg2, 10)}
          </div>

          <div>
            <Label>Company <span className="text-red-500">*</span> </Label>
            <Select value={companyName} onValueChange={setCompanyName}>
              <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                    {["kemena_holding_sdn_bhd", "eastern_park_sdn_bhd", "kenygold_resources_sdn_bhd", "kenygold_sdn_bhd", "kenygold_trading_sdn_bhd", "jayhong_sdn_bhd", "hi_rich_trading_sdn_bhd", "palmjaya_sdn_bhd", "hap_jet_galvanized_sdn_bhd", "kemena_hotel", "bandar_sinar_sdn_bhd", "eastern_roadworks_sdn_bhd", "kemena_energy_sdn_bhd", "metro_mekar_sdn_bhd", "pelita_raya_sdn_bhd", "kh_premix_sdn_bhd", "kh_industry_sdn_bhd", "bestone_quarry_sdn_bhd"].sort().map((c) => (
                        <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()).replace(/\bKh\b/g, "KH")}</SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Status <span className="text-red-500">*</span> </Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_use">In Use</SelectItem>
                <SelectItem value="in_maintenance">In Maintenance</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="no_longer_available">No Longer Available</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Road Status</Label>
            <Select value={roadStatus} onValueChange={setRoadStatus}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="on_road">On Road</SelectItem>
                <SelectItem value="off_road">Off Road</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label>Deployment <span className="text-red-500">*</span> </Label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="border rounded p-2 w-full">
              <option value="">-- Select Deployment --</option>
              {projects.map((proj) => (
                  <option key={proj.id} value={proj.id}>{proj.name}</option>
              ))}
            </select>
          </div>
        </div>



        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label>Insurance</Label>
            <Switch checked={hasInsurance} onCheckedChange={setHasInsurance} />
          </div>
          {hasInsurance && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={insuranceData.expiry_date}
                  onChange={(e) => setInsuranceData({ ...insuranceData, expiry_date: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <Label>Road Tax</Label>
            <Switch checked={hasRoadtax} onCheckedChange={setHasRoadtax} />
          </div>
          {hasRoadtax && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={roadtaxData.expiry_date}
                  onChange={(e) => setRoadtaxData({ ...roadtaxData, expiry_date: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <Label>Inspection</Label>
            <Switch checked={hasInspection} onCheckedChange={setHasInspection} />
          </div>
          {hasInspection && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={inspectionData.expiry_date}
                  onChange={(e) => setInspectionData({ ...inspectionData, expiry_date: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <Label>Brunei Permit</Label>
            <Switch checked={hasPermitBrunei} onCheckedChange={setHasPermitBrunei} />
          </div>
          {hasPermitBrunei && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={permitBrunei.expiry_date}
                  onChange={(e) => setPermitBrunei({ ...permitBrunei, expiry_date: e.target.value })}
                />
              </div>
              <div>
                <Label>License Number</Label>
                <Input
                  value={permitBrunei.license_number}
                  onChange={(e) => setPermitBrunei({ ...permitBrunei, license_number: e.target.value })}
                  maxLength={50}
                />
                {getCharacterCountDisplay(permitBrunei.license_number, 50)}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <Label>Sabah Permit</Label>
            <Switch checked={hasPermitSabah} onCheckedChange={setHasPermitSabah} />
          </div>
          {hasPermitSabah && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={permitSabah.expiry_date}
                  onChange={(e) => setPermitSabah({ ...permitSabah, expiry_date: e.target.value })}
                />
              </div>
              <div>
                <Label>License Number</Label>
                <Input
                  value={permitSabah.license_number}
                  onChange={(e) => setPermitSabah({ ...permitSabah, license_number: e.target.value })}
                  maxLength={50}
                />
                {getCharacterCountDisplay(permitSabah.license_number, 50)}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <Label>Sarawak Permit</Label>
            <Switch checked={hasPermitSarawak} onCheckedChange={setHasPermitSarawak} />
          </div>
          {hasPermitSarawak && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={permitSarawak.expiry_date}
                  onChange={(e) => setPermitSarawak({ ...permitSarawak, expiry_date: e.target.value })}
                />
              </div>
              <div>
                <Label>License Number</Label>
                <Input
                  value={permitSarawak.license_number}
                  onChange={(e) => setPermitSarawak({ ...permitSarawak, license_number: e.target.value })}
                  maxLength={50}
                />
                {getCharacterCountDisplay(permitSarawak.license_number, 50)}
              </div>
            </div>
          )}
        </div>



        <div className="flex gap-4 justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
            {vehicle ? "Update Vehicle" : "Create Vehicle"}
          </Button>
        </div>
      </form>
  );
}
